import { api } from '@/lib/api';

// Fotos direkt am Projekt (Schnell erfassen, pm-api routers/fotos.py).
// GET liefert alle Fotos des Projekts, auch die der Zonen (zone_id), neueste
// zuerst. `url` ist der Pfad für <img src>.

export interface ProjektFoto {
  id: string;
  project_id: string;
  zone_id: string | null;
  filename: string;
  storage_path: string;
  file_size: number | null;
  mime_type: string | null;
  // Aus dem EXIF-GPS beim Hochladen, sonst null.
  latitude: number | null;
  longitude: number | null;
  // Aufnahmezeit aus dem EXIF. Das liest der Server nur aus JPEG; PNG, WebP
  // und HEIC kommen ohne, ebenso Bildschirmfotos.
  taken_at: string | null;
  created_at: string;
  url: string;
}

/** Größtes Foto, das der Server annimmt: PM_FOTO_MAX_MB in pm-api, Standard 25. */
export const FOTO_MAX_MB = 25;

// Wie BILDTYPEN in pm-api (services/fotos.py). Der Server prüft den Inhalt
// selbst noch einmal; hier geht es darum, nicht erst 200 MB hochzuladen, um
// dann zu hören, dass es ein Video war.
const FOTO_TYPEN = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/webp']);
// Manche Browser kennen HEIC nicht und melden einen leeren Typ; dann zählt die Endung.
const FOTO_ENDUNG = /\.(jpe?g|png|heic|heif|webp)$/i;

/**
 * Prüft eine gewählte Datei, bevor sie hochgeht: `null`, wenn sie passt,
 * sonst der Satz für das Formular. Meldungen wie die des Servers.
 */
export function fotoPruefen(datei: Pick<File, 'name' | 'type' | 'size'>): string | null {
  const typOk = datei.type ? FOTO_TYPEN.has(datei.type.toLowerCase()) : FOTO_ENDUNG.test(datei.name);
  if (!typOk) return `„${datei.name}“ ist kein Foto. Angenommen werden JPEG, PNG, HEIC und WebP.`;
  if (datei.size === 0) return 'Die Datei ist leer.';
  if (datei.size > FOTO_MAX_MB * 1024 * 1024) return `Das Foto ist größer als ${FOTO_MAX_MB} MB.`;
  return null;
}

export const fotosApi = {
  list: async (projectId: string): Promise<ProjektFoto[]> => {
    const { data } = await api.get(`/projects/${projectId}/fotos`);
    return data;
  },

  // Die Datei geht unverändert hoch: Ein Verkleinern im Browser verlöre das
  // EXIF-GPS, aus dem das Backend die Position liest. 413 über der Grenze
  // (PM_FOTO_MAX_MB), 422 für alles, was kein JPEG, PNG, HEIC oder WebP ist.
  upload: async (projectId: string, file: File): Promise<ProjektFoto> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(`/projects/${projectId}/fotos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Ein Handyfoto über Mobilfunk braucht länger als die üblichen 30 s.
      timeout: 120_000,
    });
    return data;
  },
};
